/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
import type { Dispatch, SetStateAction } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import { generateUUID } from "@swiggy-private/dls-web-recipe-common-web/dist/components/Customise/helpers";
import { QrCodeActions, ToastType } from "@swiggy-private/portal-dineout-context";
import { ICartItems, ILocalCart, IQrCodeAction } from "@swiggy-private/portal-dineout-context";
import { UpdateCartWithActionOptions } from "types/cart";
import { showToast } from "./helper";
import { sendCustomNREvWithHeaders } from "./newrelic";
import { IInstructionAction } from "./types";
import { SPECIAL_INSTRUCTIONS } from "../constants/cart";
import { STORAGE_KEYS } from "../constants/StorageKeys";

// eslint-disable-next-line no-undef
let debounceTimeout: string | number | NodeJS.Timeout | undefined;

const debounce = (func: (...args: any[]) => Promise<void>, delay: number) => {
    return function (...args: any[]) {
        return new Promise<void>((resolve) => {
            clearTimeout(debounceTimeout);
            debounceTimeout = setTimeout(async () => {
                await func(...args);
                resolve();
            }, delay);
        });
    };
};
const transformCartItems = (cartItems: any) => {
    return cartItems.map(({ info, selectedInfo, item_instructions }: any) => {
        const selectedAddons =
            selectedInfo?.addons?.flatMap((addon: any) =>
                addon?.choices?.map((choice: any) => ({
                    choice_id: choice?.id,
                    group_id: addon?.groupId,
                })),
            ) || [];

        const selectedVariants =
            selectedInfo?.variantsV2?.variantGroups?.flatMap((variant: any) =>
                variant?.variations?.map((variation: any) => ({
                    group_id: variant?.groupId,
                    variation_id: variation?.id,
                })),
            ) || [];

        return {
            menu_item_id: info?.id,
            menu_item_uuid: generateUUID(info?.id, selectedAddons, selectedVariants),
            addons: selectedAddons,
            variants: selectedVariants,
            quantity: selectedInfo?.quantity?.value,
            instructions: item_instructions,
        };
    });
};

export const updateCartWithAction = async (options: UpdateCartWithActionOptions) => {
    const { updateLogic, helperFn, updatedDishItems, updateLocalCartInNative, clearLocalCartInNative } = options;
    const { setIsCartEmpty, setUpdatingItemId, updateCart, setShowOverlay, isCartUpdating, callbacks } = helperFn;
    let finalLocalCart: ILocalCart = {
        cartItems: [],
        restaurantId: "",
        tableId: "",
        specialInstructions: "",
    };
    try {
        const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
        finalLocalCart = localCart || { cartItems: [], restaurantId: "", tableId: "", specialInstructions: "" };
    } catch (_) {
        sendCustomNREvWithHeaders("session_storage_local_cart_error", {});
    }

    if (!finalLocalCart || !finalLocalCart.cartItems || isCartUpdating) {
        return;
    }

    const updatedCartItems = updateLogic(updatedDishItems);
    const tranformedCartItems = transformCartItems(updatedCartItems);
    // Debounce the updateCart function
    const debouncedUpdateCart = debounce(updateCart, 300);

    const updatedCart = {
        ...finalLocalCart,
        cartItems: tranformedCartItems,
    };

    const isCartEmpty = isEmpty(tranformedCartItems);

    try {
        if (isCartEmpty) {
            setIsCartEmpty(true);
        }
        setShowOverlay(true);
        await debouncedUpdateCart(updatedCart);

        // Call native bridge callbacks after successful update
        if (isCartEmpty) {
            clearLocalCartInNative?.();
        } else {
            updateLocalCartInNative?.(updatedCart);
        }

        callbacks?.onSuccess?.();
    } catch (_) {
        callbacks?.onError?.();
    } finally {
        setShowOverlay(false);
        if (setUpdatingItemId) setUpdatingItemId("");
    }
};
interface UpdateLocalCartParams {
    localCart: ILocalCart;
    updatedCartItems: ICartItems[];
    restaurantId: string | undefined;
    tableId: string | undefined;
    parentOrderId?: string;
}

interface ManageSpecialInstructionsParams {
    instructionAction: IInstructionAction;
    dispatch: Dispatch<IQrCodeAction>;
    updateCart: (payload: Record<string, unknown>) => Promise<void>;
    updateLocalCartInNative?: (cartData: Record<string, unknown>) => void;
    setShowOverlay: Dispatch<SetStateAction<boolean>>;
    isCartUpdating: boolean;
}

export const updateLocalCart = (
    { localCart, updatedCartItems, restaurantId, tableId, parentOrderId }: UpdateLocalCartParams,
    dispatch: Dispatch<IQrCodeAction>,
) => {
    const updatedPayload: ILocalCart = {
        ...localCart,
        cartItems: updatedCartItems,
        restaurantId: restaurantId || "",
        tableId: tableId || "",
        ...(localCart.specialInstructions && { specialInstructions: localCart.specialInstructions }),
        parentOrderId: parentOrderId || "",
    };
    dispatch({
        type: QrCodeActions.UPDATE_LOCAL_CART,
        payload: updatedPayload,
    });
    try {
        sessionStorage.setItem(STORAGE_KEYS.LOCAL_CART, JSON.stringify(updatedPayload));
    } catch (_) {}
};

// TODO: to combine move this in updateCartWithAction and remove this function. Single function for updating cart
export const manageSpecialInstructions = async ({
    instructionAction,
    dispatch,
    updateCart,
    updateLocalCartInNative,
    isCartUpdating,
    setShowOverlay,
}: ManageSpecialInstructionsParams) => {
    let finalLocalCart: ILocalCart = {
        cartItems: [],
        restaurantId: "",
        tableId: "",
        specialInstructions: "",
    };
    try {
        const localCart = JSON.parse(sessionStorage.getItem(STORAGE_KEYS.LOCAL_CART) || "{}");
        if (!isEmpty(instructionAction.specialInstructions) && instructionAction.action !== "remove") {
            finalLocalCart = {
                ...localCart,
                specialInstructions: instructionAction.specialInstructions?.trim(),
            };
        } else {
            finalLocalCart = {
                ...localCart,
                specialInstructions: "",
            };
        }
    } catch (_) {
        sendCustomNREvWithHeaders("session_storage_local_cart_error", {});
    }

    if (!finalLocalCart || !finalLocalCart.cartItems || isCartUpdating) {
        return;
    }

    const debouncedUpdateCart = debounce(updateCart, 0);
    try {
        setShowOverlay(true);
        await debouncedUpdateCart(finalLocalCart, 0);
        updateLocalCart(
            {
                localCart: finalLocalCart,
                updatedCartItems: finalLocalCart.cartItems,
                restaurantId: finalLocalCart.restaurantId,
                tableId: finalLocalCart.tableId,
            },
            dispatch,
        );

        // Update native cart after successful update
        updateLocalCartInNative?.(finalLocalCart as unknown as Record<string, unknown>);

        showToast({ dispatch, type: ToastType.SUCCESS, message: instructionAction.message || "" });
    } catch (_) {
        sendCustomNREvWithHeaders("update_local_cart_error", {});
        showToast({ dispatch, type: ToastType.ERROR, message: SPECIAL_INSTRUCTIONS.ERROR_MESSAGE });
    } finally {
        setShowOverlay(false);
    }
};
